import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Shield, Lock, Eye, AlertTriangle, KeyRound, Smartphone, Monitor } from 'lucide-react';

import { useToast } from '../ui/use-toast';
import { useAuth } from '@/OS/hooks/useAuth';
import { supabase } from '@/OS/integrations/supabase/client';
import { useUserSettings } from '@/OS/hooks/useUserSettings';
import { createStoredPin, isValidPin, normalizePin, verifyStoredPin } from '@/OS/lib/securityPin';
import {
  DEFAULT_SECURITY_SETTINGS,
  SECURITY_TIMEOUT_OPTIONS,
  type SecuritySettingsState,
} from '@/OS/lib/securitySettings';

const getPasswordStrength = (password: string) => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  if (/[^A-Za-z0-9]/.test(password)) strength += 25;
  return strength;
};

const getStrengthLabel = (strength: number) => {
  if (strength <= 25) return { label: 'Weak', color: 'text-red-500' };
  if (strength <= 50) return { label: 'Fair', color: 'text-orange-500' };
  if (strength <= 75) return { label: 'Good', color: 'text-yellow-500' };
  return { label: 'Strong', color: 'text-green-500' };
};

export const SecuritySettings: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { settings, updateSetting, saveSettings, isLoaded } = useUserSettings<SecuritySettingsState>(
    'security',
    DEFAULT_SECURITY_SETTINGS
  );

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinDisabling, setPinDisabling] = useState(false);

  const isElectron = typeof window !== 'undefined' && Boolean(window.electronAPI?.isElectron);
  const pinConfigured = Boolean(settings.pinHash && settings.pinSalt);
  const passwordStrength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const strengthInfo = useMemo(() => getStrengthLabel(passwordStrength), [passwordStrength]);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const resetPinForm = () => {
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
  };

  const handlePasswordChange = async () => {
    if (!user?.email) {
      toast({ title: 'Unable to change password', description: 'No email is available for this account.', variant: 'destructive' });
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Missing details', description: 'Fill out all password fields.', variant: 'destructive' });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', description: 'Confirm the same new password twice.', variant: 'destructive' });
      return;
    }

    setPasswordSaving(true);
    try {
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (reauthError) {
        throw new Error('Your current password is incorrect.');
      }

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        throw updateError;
      }

      toast({ title: 'Password updated', description: 'Your MapleOS account password has been changed.' });
      resetPasswordForm();
    } catch (error: any) {
      toast({
        title: 'Unable to update password',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handlePinSave = async () => {
    const normalizedNewPin = normalizePin(newPin);
    const normalizedConfirmPin = normalizePin(confirmPin);

    if (!isValidPin(normalizedNewPin)) {
      toast({ title: 'Invalid PIN', description: 'Use a numeric PIN between 4 and 8 digits.', variant: 'destructive' });
      return;
    }

    if (normalizedNewPin !== normalizedConfirmPin) {
      toast({ title: 'PINs do not match', description: 'Enter the same PIN twice.', variant: 'destructive' });
      return;
    }

    setPinSaving(true);
    try {
      if (pinConfigured) {
        const validCurrentPin = await verifyStoredPin(currentPin, settings);
        if (!validCurrentPin) {
          throw new Error('Your current PIN is incorrect.');
        }
      }

      const storedPin = await createStoredPin(normalizedNewPin);
      await saveSettings({
        ...storedPin,
        autoLockEnabled: settings.autoLockEnabled,
        autoLockMinutes: settings.autoLockMinutes,
        fullscreenPinLockEnabled: settings.fullscreenPinLockEnabled,
      });

      toast({
        title: pinConfigured ? 'PIN updated' : 'PIN created',
        description: pinConfigured
          ? 'Use the new PIN to unlock MapleOS and leave locked full screen mode.'
          : 'You can now lock MapleOS and require a PIN to exit full screen.',
      });
      resetPinForm();
    } catch (error: any) {
      toast({
        title: 'Unable to save PIN',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPinSaving(false);
    }
  };

  const handlePinDisable = async () => {
    if (!pinConfigured) {
      return;
    }

    setPinDisabling(true);
    try {
      const validCurrentPin = await verifyStoredPin(currentPin, settings);
      if (!validCurrentPin) {
        throw new Error('Enter your current PIN to disable it.');
      }

      await saveSettings({
        pinHash: '',
        pinSalt: '',
        pinIterations: DEFAULT_SECURITY_SETTINGS.pinIterations,
        autoLockEnabled: false,
        fullscreenPinLockEnabled: false,
      });

      toast({
        title: 'PIN disabled',
        description: 'Screen lock and fullscreen lock are now turned off.',
      });
      resetPinForm();
    } catch (error: any) {
      toast({
        title: 'Unable to disable PIN',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPinDisabling(false);
    }
  };

  const requestLockNow = () => {
    if (!pinConfigured) {
      toast({
        title: 'Set a PIN first',
        description: 'A MapleOS PIN is required before screen lock can be used.',
        variant: 'destructive',
      });
      return;
    }

    window.dispatchEvent(new CustomEvent('mapleos:lock-screen'));
  };

  const handleAutoLockToggle = async (checked: boolean) => {
    if (checked && !pinConfigured) {
      toast({
        title: 'Set a PIN first',
        description: 'Automatic screen lock requires a MapleOS PIN.',
        variant: 'destructive',
      });
      return;
    }

    await updateSetting('autoLockEnabled', checked);
  };

  const handleFullscreenLockToggle = async (checked: boolean) => {
    if (checked && !pinConfigured) {
      toast({
        title: 'Set a PIN first',
        description: 'Full screen lock requires a MapleOS PIN.',
        variant: 'destructive',
      });
      return;
    }

    if (checked && !isElectron) {
      toast({
        title: 'Electron only',
        description: 'Full screen exit protection is only available in the Electron desktop app.',
        variant: 'destructive',
      });
      return;
    }

    await updateSetting('fullscreenPinLockEnabled', checked);
    if (checked) {
      window.dispatchEvent(new CustomEvent('mapleos:fullscreen-lock-enabled'));
    }
  };

  const securityStatus = pinConfigured || settings.autoLockEnabled || settings.fullscreenPinLockEnabled;

  return (
    <div className="h-full overflow-auto bg-background p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="mb-6">
          <h1 className="mb-2 text-2xl font-bold text-foreground">Security Settings</h1>
          <p className="text-muted-foreground">Configure MapleOS password, screen lock, and full screen access controls.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield size={20} />
              Security Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${securityStatus ? 'bg-green-500' : 'bg-amber-500'} animate-pulse`} />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-300">
                    {securityStatus ? 'MapleOS protection active' : 'Basic account protection only'}
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-400">
                    {pinConfigured
                      ? 'PIN protection is configured for local screen and kiosk-style locking.'
                      : 'Set a PIN to enable MapleOS screen lock and full screen exit control.'}
                  </div>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200">
                {pinConfigured ? 'Protected' : 'Password only'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="font-medium">Account Password</div>
                <div className="text-green-600">Active</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="font-medium">Screen Lock</div>
                <div className={settings.autoLockEnabled ? 'text-green-600' : 'text-muted-foreground'}>
                  {settings.autoLockEnabled ? 'Enabled' : 'Off'}
                </div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="font-medium">PIN Lock</div>
                <div className={pinConfigured ? 'text-green-600' : 'text-muted-foreground'}>
                  {pinConfigured ? 'Configured' : 'Not set'}
                </div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="font-medium">Full Screen Guard</div>
                <div className={settings.fullscreenPinLockEnabled ? 'text-green-600' : 'text-muted-foreground'}>
                  {settings.fullscreenPinLockEnabled ? 'Enabled' : 'Off'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock size={20} />
              Password Settings
            </CardTitle>
            <CardDescription>
              Change the CanXP account password used to sign into MapleOS.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
              MapleOS uses your signed-in CanXP account for authentication. Enter your current password to confirm the change.
            </div>

            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                placeholder="Enter current password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="Enter new password"
              />
              {newPassword ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Password Strength</span>
                    <span className={strengthInfo.color}>{strengthInfo.label}</span>
                  </div>
                  <Progress value={passwordStrength} className="h-2" />
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Confirm new password"
              />
            </div>

            <Button onClick={() => void handlePasswordChange()} disabled={passwordSaving || !isLoaded}>
              {passwordSaving ? 'Updating...' : 'Update Password'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound size={20} />
              PIN & User Lock Controls
            </CardTitle>
            <CardDescription>
              Set a local MapleOS PIN for screen unlock and optional full screen exit protection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
              This PIN is separate from your account password. You must know the current PIN to change or disable it later.
            </div>

            {pinConfigured ? (
              <div className="space-y-2">
                <Label htmlFor="current-pin">Current PIN</Label>
                <Input
                  id="current-pin"
                  type="password"
                  inputMode="numeric"
                  value={currentPin}
                  onChange={(event) => setCurrentPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter current PIN"
                />
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-pin">{pinConfigured ? 'New PIN' : 'Create PIN'}</Label>
                <Input
                  id="new-pin"
                  type="password"
                  inputMode="numeric"
                  value={newPin}
                  onChange={(event) => setNewPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="4 to 8 digits"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pin">Confirm PIN</Label>
                <Input
                  id="confirm-pin"
                  type="password"
                  inputMode="numeric"
                  value={confirmPin}
                  onChange={(event) => setConfirmPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Re-enter PIN"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => void handlePinSave()} disabled={pinSaving || !isLoaded}>
                {pinSaving ? 'Saving...' : pinConfigured ? 'Update PIN' : 'Set PIN'}
              </Button>
              {pinConfigured ? (
                <Button variant="outline" onClick={() => void handlePinDisable()} disabled={pinDisabling || !currentPin}>
                  {pinDisabling ? 'Disabling...' : 'Disable PIN'}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye size={20} />
              Screen Lock
            </CardTitle>
            <CardDescription>
              Lock MapleOS automatically after inactivity or immediately on demand.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="auto-lock">Enable automatic screen lock</Label>
                <p className="text-sm text-muted-foreground">A PIN is required to unlock once MapleOS is locked.</p>
              </div>
              <Switch id="auto-lock" checked={settings.autoLockEnabled} onCheckedChange={(checked) => void handleAutoLockToggle(checked)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lock-timeout">Lock after inactivity</Label>
              <Select
                value={String(settings.autoLockMinutes)}
                onValueChange={(value) => void updateSetting('autoLockMinutes', Number(value))}
                disabled={!settings.autoLockEnabled}
              >
                <SelectTrigger id="lock-timeout">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECURITY_TIMEOUT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" onClick={requestLockNow} disabled={!pinConfigured}>
              Lock Screen Now
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor size={20} />

              Lock to MapleOS Full Screen
            </CardTitle>
            <CardDescription>
              In Electron, require the MapleOS PIN before students or guests can leave full screen mode.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="fullscreen-pin-lock">Require PIN to exit full screen</Label>
                <p className="text-sm text-muted-foreground">
                  Best for kiosk or classroom use. MapleOS will keep itself in full screen until an authorized user enters the PIN.
                </p>
              </div>
              <Switch
                id="fullscreen-pin-lock"
                checked={settings.fullscreenPinLockEnabled}
                onCheckedChange={(checked) => void handleFullscreenLockToggle(checked)}
                disabled={!isElectron}
              />
            </div>

            <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
              {isElectron
                ? 'When enabled, MapleOS will intercept F11 while full screen is active and ask for the PIN before leaving full screen.'
                : 'This feature is available only in the Electron desktop app.'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone size={20} />
              Additional Security Settings
            </CardTitle>
            <CardDescription>
              These controls are still lightweight, but they now persist per user.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="two-factor">Two-Factor Authentication</Label>
                <p className="text-sm text-muted-foreground">Placeholder toggle for future MapleOS account hardening.</p>
              </div>
              <Switch id="two-factor" checked={settings.twoFactorEnabled} onCheckedChange={(checked) => void updateSetting('twoFactorEnabled', checked)} />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="privacy-mode">Enhanced Privacy Mode</Label>
                <p className="text-sm text-muted-foreground">Store your preference for reduced telemetry and local-first use.</p>
              </div>
              <Switch id="privacy-mode" checked={settings.privacyMode} onCheckedChange={(checked) => void updateSetting('privacyMode', checked)} />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="allow-guests">Allow guest access</Label>
                <p className="text-sm text-muted-foreground">Stored for future local multi-user and guest session controls.</p>
              </div>
              <Switch id="allow-guests" checked={settings.allowGuests} onCheckedChange={(checked) => void updateSetting('allowGuests', checked)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle size={20} />
              Security Level
            </CardTitle>
            <CardDescription>
              Store the overall MapleOS security posture for this user.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="security-level">Security Level</Label>
              <Select value={settings.securityLevel} onValueChange={(value) => void updateSetting('securityLevel', value as SecuritySettingsState['securityLevel'])}>

                <SelectTrigger id="security-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Maximum convenience</SelectItem>
                  <SelectItem value="medium">Medium - Balanced security</SelectItem>
                  <SelectItem value="high">High - Maximum protection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
